/**
 * ResearchScopeDocPanel Component
 * 
 * Modern sliding panel for editing a Research Scope.
 * Matches the styling of CreateResearchScopeWizard with:
 * - Gradient header
 * - Tab navigation  
 * - Modern form inputs
 * - Drag & drop file upload
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Spinner,
  SpinnerSize,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton
} from '@fluentui/react';
import {
  DismissRegular,
  DocumentRegular,
  DeleteRegular,
  CheckmarkCircleFilled,
  ArrowSyncRegular,
  CloudArrowUpRegular,
  DeleteDismissRegular,
  SaveRegular,
  PeopleRegular,
  SettingsRegular,
  InfoRegular,
  BookSearchRegular
} from '@fluentui/react-icons';
import {
  getResearchScope,
  getResearchScopeStatus,
  uploadDocument,
  deleteDocument,
  deleteResearchScope,
  updateResearchScope,
  ResearchScopeDetail,
  ScopeFile,
  ScopeStatus
} from '../../api/researchScopeApi';
import { authenticatedFetch } from '../../utils/authApi';

const SUPPORTED_EXTENSIONS = [
  '.pdf', '.docx', '.doc', '.xlsx', '.xls', 
  '.pptx', '.ppt', '.txt', '.png', '.jpg', '.jpeg', '.gif', '.webp'
];

type TabKey = 'details' | 'documents' | 'instructions';

interface ResearchScopeDocPanelProps {
  scopeId: string;
  isOpen: boolean;
  onClose: () => void;
  onOpenSharing?: () => void;
  onDeleted?: () => void;
  onUpdated?: () => void;
}

export const ResearchScopeDocPanel: React.FC<ResearchScopeDocPanelProps> = ({
  scopeId,
  isOpen,
  onClose,
  onOpenSharing,
  onDeleted,
  onUpdated
}) => {
  const [scope, setScope] = useState<ResearchScopeDetail | null>(null);
  const [status, setStatus] = useState<ScopeStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  
  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [syncingInstructions, setSyncingInstructions] = useState(false);
  const [regeneratingInstructions, setRegeneratingInstructions] = useState(false);
  
  // Editable fields
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editRetrievalInstructions, setEditRetrievalInstructions] = useState('');
  const [editAnswerInstructions, setEditAnswerInstructions] = useState('');
  const [editReasoningEffort, setEditReasoningEffort] = useState('low');
  const [editMaxOutputSize, setEditMaxOutputSize] = useState(5000);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const statusPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Load scope data
  const loadScope = useCallback(async () => {
    if (!scopeId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const [scopeData, statusData] = await Promise.all([
        getResearchScope(scopeId),
        getResearchScopeStatus(scopeId)
      ]);
      
      setScope(scopeData);
      setStatus(statusData);
      
      // Initialize edit fields with current values (with null checks)
      if (scopeData) {
        setEditName(scopeData.name || '');
        setEditDescription(scopeData.description || '');
        setEditRetrievalInstructions(scopeData.retrieval_instructions || '');
        setEditAnswerInstructions(scopeData.answer_instructions || '');
        setEditReasoningEffort(scopeData.reasoning_effort || 'low');
        setEditMaxOutputSize(scopeData.max_output_size || 5000);
        setHasChanges(false);
      } else {
        setError('Research scope not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scope');
    } finally {
      setIsLoading(false);
    }
  }, [scopeId]);

  // Initial load
  useEffect(() => {
    if (isOpen && scopeId) {
      loadScope();
      setActiveTab('details');
    }
  }, [isOpen, scopeId, loadScope]);

  // Check for changes
  const checkForChanges = useCallback(() => {
    if (!scope) return false;
    return (
      editName !== (scope.name || '') ||
      editDescription !== (scope.description || '') ||
      editRetrievalInstructions !== (scope.retrieval_instructions || '') ||
      editAnswerInstructions !== (scope.answer_instructions || '') ||
      editReasoningEffort !== (scope.reasoning_effort || 'low') ||
      editMaxOutputSize !== (scope.max_output_size || 5000)
    );
  }, [scope, editName, editDescription, editRetrievalInstructions, editAnswerInstructions, editReasoningEffort, editMaxOutputSize]);

  useEffect(() => {
    setHasChanges(checkForChanges());
  }, [checkForChanges]);

  // Poll for status updates when indexing
  useEffect(() => {
    if (!isOpen || !scopeId || status?.status !== 'indexing') {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
      return;
    }

    statusPollingRef.current = setInterval(async () => {
      try {
        const newStatus = await getResearchScopeStatus(scopeId);
        setStatus(newStatus);
        
        if (newStatus.status !== 'indexing') {
          if (statusPollingRef.current) {
            clearInterval(statusPollingRef.current);
            statusPollingRef.current = null;
          }
        }
      } catch (err) {
        console.error('Status polling failed:', err);
      }
    }, 5000);

    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
      }
    };
  }, [isOpen, scopeId, status?.status]);

  // Save changes
  const handleSave = async () => {
    if (!scope || !hasChanges) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      const updatedScope = await updateResearchScope(scope.id, {
        name: editName.trim(),
        description: editDescription.trim(),
        retrieval_instructions: editRetrievalInstructions.trim(),
        answer_instructions: editAnswerInstructions.trim(),
        reasoning_effort: editReasoningEffort,
        max_output_size: editMaxOutputSize
      });
      
      setScope(updatedScope);
      setHasChanges(false);
      setSuccessMessage('Changes saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Regenerate instructions with latest comprehensive templates
  const regenerateInstructions = async () => {
    if (!scope?.id) return;
    
    try {
      setRegeneratingInstructions(true);
      setError(null);
      
      const response = await authenticatedFetch(
        `/api/research-scopes/${encodeURIComponent(scope.id)}/regenerate-instructions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sync_to_kb: true })
        }
      );
      const data = await response.json();
      
      if (data.success) {
        // Update local state with new instructions
        setEditRetrievalInstructions(data.retrieval_instructions || '');
        setEditAnswerInstructions(data.answer_instructions || '');
        setSuccessMessage('✅ Instructions regenerated with latest comprehensive templates!');
        setTimeout(() => setSuccessMessage(null), 5000);
        onUpdated?.();
      } else {
        setError(data.error || 'Failed to regenerate instructions');
      }
    } catch (err) {
      console.error('Failed to regenerate instructions:', err);
      setError('Failed to regenerate instructions');
    } finally {
      setRegeneratingInstructions(false);
    }
  };

  // Sync instructions to Knowledge Base
  const syncInstructionsToKB = async () => {
    if (!scope?.knowledge_base_name) {
      setError('Knowledge Base name is required to sync instructions');
      return;
    }
    
    if (!editRetrievalInstructions && !editAnswerInstructions) {
      setError('At least one instruction (retrieval or answer) is required');
      return;
    }
    
    try {
      setSyncingInstructions(true);
      setError(null);
      
      const response = await authenticatedFetch(
        `/api/admin/agentic-retrieval/knowledge-bases/${encodeURIComponent(scope.knowledge_base_name)}/update-instructions`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            retrieval_instructions: editRetrievalInstructions || '',
            answer_instructions: editAnswerInstructions || '',
            output_mode: 'answerSynthesis',
            reasoning_effort: editReasoningEffort || 'low'
          })
        }
      );
      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`✅ Instructions synced to Knowledge Base "${scope.knowledge_base_name}"`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(data.error || 'Failed to sync instructions to Knowledge Base');
      }
    } catch (err) {
      console.error('Failed to sync instructions:', err);
      setError('Failed to sync instructions to Knowledge Base');
    } finally {
      setSyncingInstructions(false);
    }
  };

  // File handling
  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      return SUPPORTED_EXTENSIONS.includes(ext);
    });

    if (validFiles.length < newFiles.length) {
      setError(`Some files were skipped (unsupported format)`);
    }

    setPendingFiles(prev => [...prev, ...validFiles]);
  };

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadFiles = async () => {
    if (!scope || pendingFiles.length === 0) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    for (let i = 0; i < pendingFiles.length; i++) {
      try {
        await uploadDocument(scope.id, pendingFiles[i]);
        setUploadProgress(((i + 1) / pendingFiles.length) * 100);
      } catch (err) {
        console.error(`Failed to upload ${pendingFiles[i].name}:`, err);
        setError(`Failed to upload ${pendingFiles[i].name}`);
      }
    }

    setPendingFiles([]);
    setIsUploading(false);
    await loadScope(); // Refresh file list
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!scope) return;
    
    setDeletingFileId(fileId);
    setError(null);

    try {
      await deleteDocument(scope.id, fileId);
      await loadScope();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDeleteScope = async () => {
    if (!scope) return;
    
    setIsDeleting(true);
    setError(null);

    try {
      await deleteResearchScope(scope.id);
      setShowDeleteConfirm(false);
      onClose();
      onDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scope');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileStatusIcon = (file: ScopeFile) => {
    switch (file.processing_status) {
      case 'indexed':
        return <CheckmarkCircleFilled style={{ color: '#10b981', fontSize: '16px' }} />;
      case 'indexing':
        return <ArrowSyncRegular style={{ color: '#3b82f6', fontSize: '16px' }} />;
      default:
        return <CloudArrowUpRegular style={{ color: '#f59e0b', fontSize: '16px' }} />;
    }
  };

  const getStatusBadge = () => {
    if (!status) return null;
    
    const statusConfig: Record<string, { bg: string; color: string; text: string }> = {
      empty: { bg: '#f3f4f6', color: '#6b7280', text: 'No Documents' },
      indexing: { bg: '#dbeafe', color: '#2563eb', text: 'Indexing...' },
      ready: { bg: '#d1fae5', color: '#059669', text: 'Ready' },
      error: { bg: '#fee2e2', color: '#dc2626', text: 'Error' }
    };
    
    const config = statusConfig[status.status] || statusConfig.empty;
    
    return (
      <span style={{
        backgroundColor: config.bg,
        color: config.color,
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: '20px'
      }}>
        {config.text}
      </span>
    );
  };

  const tabs = [
    { key: 'details' as TabKey, label: 'Details', icon: InfoRegular },
    { key: 'documents' as TabKey, label: 'Documents', icon: DocumentRegular },
    { key: 'instructions' as TabKey, label: 'Instructions', icon: SettingsRegular }
  ];

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onDismiss={onClose}
        isBlocking={isSaving || isUploading}
        styles={{
          main: {
            maxWidth: '640px',
            width: '90vw',
            maxHeight: '90vh',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0,0,0,0.2)'
          },
          scrollableContent: {
            overflow: 'hidden'
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '80vh', maxHeight: '650px' }}>
          {/* Header with gradient */}
          <div style={{
            padding: '24px 28px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              <DismissRegular style={{ color: 'white', fontSize: '18px' }} />
            </button>

            {/* Title area */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Research Scope Icon */}
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: 'rgba(255,255,255,0.2)',
                  border: '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <BookSearchRegular style={{ fontSize: '28px', color: 'white' }} />
              </div>
              <div style={{ flex: 1 }}>
                <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 600 }}>
                  Edit Research Scope
                </h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' }}>
                  {getStatusBadge()}
                  <span style={{ opacity: 0.9, fontSize: '13px' }}>
                    {scope?.file_count || 0} documents
                  </span>
                  {hasChanges && (
                    <span style={{
                      background: 'rgba(255,255,255,0.25)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: 500
                    }}>
                      Unsaved
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Tab navigation */}
            <div style={{ display: 'flex', gap: '4px', marginTop: '20px' }}>
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      background: isActive ? 'rgba(255,255,255,0.25)' : 'transparent',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: isActive ? 600 : 400,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: isActive ? 1 : 0.7,
                      transition: 'all 0.2s'
                    }}
                  >
                    <Icon style={{ fontSize: '16px' }} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Messages */}
          {(error || successMessage) && (
            <div style={{ padding: '16px 28px 0' }}>
              {error && (
                <div style={{
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '10px',
                  color: '#dc2626',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>{error}</span>
                  <button
                    onClick={() => setError(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                  >
                    <DismissRegular />
                  </button>
                </div>
              )}
              {successMessage && (
                <div style={{
                  padding: '12px 16px',
                  background: '#d1fae5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '10px',
                  color: '#059669',
                  fontSize: '14px'
                }}>
                  <CheckmarkCircleFilled style={{ marginRight: '8px' }} />
                  {successMessage}
                </div>
              )}
            </div>
          )}

          {/* Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <Spinner size={SpinnerSize.large} label="Loading scope..." />
              </div>
            ) : (
              <>
                {/* Details Tab */}
                {activeTab === 'details' && (
                  <div style={{ maxWidth: '500px' }}>
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Scope Name <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="e.g., Q4 Financial Reports"
                        style={{
                          width: '100%',
                          padding: '14px 18px',
                          fontSize: '15px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          outline: 'none',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 0 0 4px rgba(102,126,234,0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Description
                      </label>
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Describe what this research scope contains..."
                        rows={6}
                        style={{
                          width: '100%',
                          padding: '14px 18px',
                          fontSize: '15px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          outline: 'none',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          lineHeight: 1.5,
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 0 0 4px rgba(102,126,234,0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Documents Tab */}
                {activeTab === 'documents' && (
                  <div>
                    {/* Drop zone */}
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleFileDrop}
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        border: `2px dashed ${isDragging ? '#667eea' : '#d1d5db'}`,
                        borderRadius: '16px',
                        padding: '32px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: isDragging ? 'rgba(102,126,234,0.05)' : '#fafafa',
                        transition: 'all 0.2s',
                        marginBottom: '24px'
                      }}
                    >
                      <CloudArrowUpRegular style={{
                        fontSize: '40px',
                        color: isDragging ? '#667eea' : '#9ca3af',
                        marginBottom: '12px'
                      }} />
                      <p style={{ margin: 0, color: '#374151', fontWeight: 500 }}>
                        {isDragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
                      </p>
                      <p style={{ margin: '8px 0 0', color: '#9ca3af', fontSize: '13px' }}>
                        PDF, Word, Excel, PowerPoint, Images
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={SUPPORTED_EXTENSIONS.join(',')}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                      />
                    </div>

                    {/* Pending files */}
                    {pendingFiles.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                            Ready to Upload ({pendingFiles.length})
                          </h4>
                          <button
                            onClick={handleUploadFiles}
                            disabled={isUploading}
                            style={{
                              padding: '8px 16px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              border: 'none',
                              borderRadius: '8px',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '13px',
                              cursor: isUploading ? 'not-allowed' : 'pointer',
                              opacity: isUploading ? 0.6 : 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            {isUploading ? (
                              <Spinner size={SpinnerSize.xSmall} />
                            ) : (
                              <CloudArrowUpRegular />
                            )}
                            {isUploading ? `Uploading ${Math.round(uploadProgress)}%` : 'Upload All'}
                          </button>
                        </div>
                        <div style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          overflow: 'hidden'
                        }}>
                          {pendingFiles.map((file, index) => (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 16px',
                                borderBottom: index < pendingFiles.length - 1 ? '1px solid #f3f4f6' : 'none',
                                background: '#fefce8'
                              }}
                            >
                              <DocumentRegular style={{ color: '#f59e0b', marginRight: '12px', fontSize: '18px' }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: '#1f2937',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {file.name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {formatFileSize(file.size)}
                                </div>
                              </div>
                              <button
                                onClick={() => removePendingFile(index)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: '#9ca3af',
                                  padding: '4px'
                                }}
                              >
                                <DeleteRegular style={{ fontSize: '16px' }} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Uploaded files */}
                    <div>
                      <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                        Uploaded Documents ({scope?.files?.length || 0})
                      </h4>
                      {(!scope || !scope.files || scope.files.length === 0) ? (
                        <div style={{
                          padding: '40px',
                          textAlign: 'center',
                          background: '#f9fafb',
                          borderRadius: '12px',
                          color: '#6b7280'
                        }}>
                          <DocumentRegular style={{ fontSize: '36px', marginBottom: '12px', opacity: 0.5 }} />
                          <p style={{ margin: 0 }}>No documents uploaded yet</p>
                        </div>
                      ) : (
                        <div style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          maxHeight: '280px',
                          overflowY: 'auto'
                        }}>
                          {(scope?.files || []).map((file, index) => (
                            <div
                              key={file.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 16px',
                                borderBottom: index < (scope?.files?.length || 0) - 1 ? '1px solid #f3f4f6' : 'none',
                                background: deletingFileId === file.id ? '#fef2f2' : 'white'
                              }}
                            >
                              <div style={{ marginRight: '12px' }}>
                                {getFileStatusIcon(file)}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: '#1f2937',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {file.filename}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {formatFileSize(file.size_bytes)}
                                  {file.requires_ocr && ' • OCR'}
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteFile(file.id)}
                                disabled={deletingFileId !== null}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: deletingFileId ? 'not-allowed' : 'pointer',
                                  color: '#9ca3af',
                                  padding: '6px',
                                  borderRadius: '6px',
                                  transition: 'all 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                              >
                                {deletingFileId === file.id ? (
                                  <Spinner size={SpinnerSize.xSmall} />
                                ) : (
                                  <DeleteRegular style={{ fontSize: '16px' }} />
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Instructions Tab */}
                {activeTab === 'instructions' && (
                  <div>
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
                      borderRadius: '12px',
                      marginBottom: '24px',
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'flex-start'
                    }}>
                      <InfoRegular style={{ color: '#667eea', fontSize: '20px', flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ color: '#4b5563', fontSize: '14px', lineHeight: 1.5 }}>
                        These instructions guide the AI when searching and answering questions 
                        about your documents. Customize them for better results.
                      </div>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Retrieval Instructions
                      </label>
                      <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#6b7280' }}>
                        How should the AI search for relevant information?
                      </p>
                      <textarea
                        value={editRetrievalInstructions}
                        onChange={(e) => setEditRetrievalInstructions(e.target.value)}
                        placeholder="E.g., Focus on technical specifications, code examples, and implementation details..."
                        rows={5}
                        style={{
                          width: '100%',
                          padding: '14px 18px',
                          fontSize: '14px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          outline: 'none',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          lineHeight: 1.5,
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 0 0 4px rgba(102,126,234,0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px'
                      }}>
                        Answer Instructions
                      </label>
                      <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#6b7280' }}>
                        How should the AI format its answers?
                      </p>
                      <textarea
                        value={editAnswerInstructions}
                        onChange={(e) => setEditAnswerInstructions(e.target.value)}
                        placeholder="E.g., Be concise and use bullet points. Include code examples when relevant..."
                        rows={5}
                        style={{
                          width: '100%',
                          padding: '14px 18px',
                          fontSize: '14px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          outline: 'none',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          lineHeight: 1.5,
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 0 0 4px rgba(102,126,234,0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    {/* Reasoning Effort & Max Output Settings */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '16px',
                      marginTop: '24px'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          🧠 Reasoning Effort
                        </label>
                        <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#6b7280' }}>
                          Higher effort = better quality, slower response
                        </p>
                        <select
                          value={editReasoningEffort}
                          onChange={(e) => setEditReasoningEffort(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            fontSize: '14px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '10px',
                            outline: 'none',
                            background: 'white',
                            cursor: 'pointer',
                            transition: 'border-color 0.2s'
                          }}
                        >
                          <option value="minimal">Minimal (Fastest - basic retrieval)</option>
                          <option value="low">Low (Fast - ~16s)</option>
                          <option value="medium">Medium (Thorough - ~21s)</option>
                        </select>
                      </div>
                      
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          📏 Max Output Size (tokens)
                        </label>
                        <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#6b7280' }}>
                          Limit the response length
                        </p>
                        <input
                          type="number"
                          value={editMaxOutputSize}
                          onChange={(e) => setEditMaxOutputSize(parseInt(e.target.value) || 5000)}
                          min={1000}
                          max={10000}
                          step={500}
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            fontSize: '14px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '10px',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s'
                          }}
                        />
                      </div>
                    </div>

                    {/* Character limit warning */}
                    {((editRetrievalInstructions?.length || 0) > 4096 || 
                      (editAnswerInstructions?.length || 0) > 4096) && (
                      <div style={{
                        marginTop: '16px',
                        padding: '12px 16px',
                        background: '#fef3c7',
                        borderRadius: '8px',
                        color: '#92400e',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        ⚠️ Azure limits instructions to 4096 characters each. Instructions will be truncated when synced.
                      </div>
                    )}

                    {/* Instructions Action Buttons */}
                    <div style={{ 
                      marginTop: '24px',
                      paddingTop: '20px',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px'
                    }}>
                      {/* Regenerate Instructions Button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                          onClick={regenerateInstructions}
                          disabled={regeneratingInstructions || !scope?.id}
                          style={{
                            padding: '12px 20px',
                            background: regeneratingInstructions 
                              ? '#e5e7eb' 
                              : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            border: 'none',
                            borderRadius: '10px',
                            color: regeneratingInstructions ? '#9ca3af' : 'white',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: regeneratingInstructions ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s'
                          }}
                        >
                          {regeneratingInstructions ? (
                            <>
                              <Spinner size={SpinnerSize.xSmall} />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <ArrowSyncRegular style={{ fontSize: '18px' }} />
                              Regenerate with Best Practices
                            </>
                          )}
                        </button>
                        <span style={{ color: '#6b7280', fontSize: '13px' }}>
                          Replace with latest comprehensive instruction templates
                        </span>
                      </div>

                      {/* Sync Instructions Button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                          onClick={syncInstructionsToKB}
                          disabled={syncingInstructions || !scope?.knowledge_base_name || (!editRetrievalInstructions && !editAnswerInstructions)}
                          style={{
                            padding: '12px 20px',
                            background: syncingInstructions || !scope?.knowledge_base_name 
                              ? '#e5e7eb' 
                              : 'linear-gradient(135deg, #16a085 0%, #1abc9c 100%)',
                            border: 'none',
                            borderRadius: '10px',
                            color: syncingInstructions || !scope?.knowledge_base_name ? '#9ca3af' : 'white',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: syncingInstructions || !scope?.knowledge_base_name ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s'
                          }}
                        >
                          {syncingInstructions ? (
                            <>
                              <Spinner size={SpinnerSize.xSmall} />
                              Syncing...
                            </>
                          ) : (
                            <>
                              <CloudArrowUpRegular style={{ fontSize: '18px' }} />
                              Sync Instructions to Azure KB
                            </>
                          )}
                        </button>
                        <span style={{ color: '#6b7280', fontSize: '13px' }}>
                          Updates instructions directly in Azure Knowledge Base
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 28px',
            borderTop: '1px solid #e5e7eb',
            background: '#f9fafb',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              style={{
                padding: '12px 24px',
                background: hasChanges ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e5e7eb',
                border: 'none',
                borderRadius: '10px',
                color: hasChanges ? 'white' : '#9ca3af',
                fontWeight: 600,
                fontSize: '14px',
                cursor: hasChanges && !isSaving ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              {isSaving ? (
                <Spinner size={SpinnerSize.xSmall} />
              ) : (
                <SaveRegular style={{ fontSize: '18px' }} />
              )}
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>

            {/* Share button */}
            {onOpenSharing && (
              <button
                onClick={onOpenSharing}
                style={{
                  padding: '12px 20px',
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  color: '#374151',
                  fontWeight: 500,
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.color = '#667eea';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = '#374151';
                }}
              >
                <PeopleRegular style={{ fontSize: '18px' }} />
                Share
              </button>
            )}

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Delete button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                border: '2px solid #fecaca',
                borderRadius: '10px',
                color: '#dc2626',
                fontWeight: 500,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fef2f2';
                e.currentTarget.style.borderColor = '#dc2626';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#fecaca';
              }}
            >
              <DeleteDismissRegular style={{ fontSize: '18px' }} />
              Delete Scope
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={!showDeleteConfirm}
        onDismiss={() => setShowDeleteConfirm(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete Research Scope?',
          subText: `Are you sure you want to delete "${scope?.name}"? This will permanently delete all documents, the knowledge base, and all associated data. This action cannot be undone.`
        }}
        modalProps={{
          isBlocking: true,
          styles: { main: { maxWidth: 450, borderRadius: '12px' } }
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={handleDeleteScope}
            text={isDeleting ? 'Deleting...' : 'Delete'}
            disabled={isDeleting}
            styles={{
              root: { backgroundColor: '#dc2626', borderColor: '#dc2626', borderRadius: '8px' },
              rootHovered: { backgroundColor: '#b91c1c', borderColor: '#b91c1c' },
              rootPressed: { backgroundColor: '#b91c1c', borderColor: '#b91c1c' }
            }}
          />
          <DefaultButton
            onClick={() => setShowDeleteConfirm(false)}
            text="Cancel"
            disabled={isDeleting}
            styles={{ root: { borderRadius: '8px' } }}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default ResearchScopeDocPanel;
